import { gsap } from 'gsap';

class HeroAnimation {
  constructor() {
    this.hero = document.querySelector('.ns-hero');
    if (!this.hero) return;

    this.duration = 3000;
    this.contentDelay = 2000;

    this.overlay = this.hero.querySelector('.ns-hero__overlay-gradient');
    this.videoContainer = this.hero.querySelector('.ns-hero__video-container');

    this.desktopSvg = this.overlay.querySelector('.ns-hero__overlay-svg--desktop');
    this.mobileSvg = this.overlay.querySelector('.ns-hero__overlay-svg--mobile');

    this.isMobile = window.innerWidth < 992;

    this.desktopSvg.style.display = 'none';
    this.mobileSvg.style.display = 'none';

    this.updateActiveSvg();

    this.updatePolygonSelectors();

    this.overlayContent = this.hero.querySelector('.ns-hero__overlay');

    window.addEventListener('resize', () => {
      this.isMobile = window.innerWidth < 992;
      this.updatePolygonSelectors();
    });

    this.init();
  }

  updateActiveSvg() {
    this.desktopSvg.style.display = this.isMobile ? 'none' : 'block';
    this.mobileSvg.style.display = this.isMobile ? 'block' : 'none';
  }

  updatePolygonSelectors() {
    const activeSvg = this.isMobile ? this.mobileSvg : this.desktopSvg;

    this.topPolygon = activeSvg.querySelector('.ns-hero__overlay-polygon--top');
    this.leftPolygon = activeSvg.querySelector('.ns-hero__overlay-polygon--left');
    this.rightPolygon = activeSvg.querySelector('.ns-hero__overlay-polygon--right');
  }

  init() {
    setTimeout(() => {
      this.animate();
    }, 200);
  }

  setupTextAnimation(startDelay) {
    const tl = gsap.timeline({
      delay: startDelay / 1000
    });

    // Berechne die exakte Dauer für Phase 2
    const phase2Duration = (this.duration - startDelay) / 1000; // Konvertiere zu Sekunden

    if (this.isMobile) {
      tl.set(this.overlayContent, {
        clipPath: 'polygon(0% 20%,100% 60%,100% 100%, 0% 100%)',
      }).to(this.overlayContent, {
        clipPath: 'polygon(0% 20%, 0% 100%, 100% 100%, 0% 100%)',
        duration: phase2Duration,
        ease: "power3.inOut"
      });
    } else {
      tl.set(this.overlayContent, {
        clipPath: 'polygon(0% 0%, 65% 50%, 65% 100%, 0% 100%)',
      }).to(this.overlayContent, {
        clipPath: 'polygon(0% 0%, 0% 100%, 65% 100%, 0% 100%)',
        duration: phase2Duration,
        ease: "power3.inOut"
      });
    }
  }

  animate() {
    const startTime = performance.now();
    const firstPhaseEnd = this.duration * 0.5;
    const secondPhaseStart = this.duration * 0.4;

    // Starte die Text-Animation genau zu Beginn der Phase 2
    this.setupTextAnimation(secondPhaseStart);

    const animate = (currentTime) => {
      const elapsed = currentTime - startTime;

      this.videoContainer.querySelector('video').play();

      if (elapsed >= this.duration) {
        this.desktopSvg.style.display = this.isMobile ? 'none' : 'block';
        this.mobileSvg.style.display = this.isMobile ? 'block' : 'none';
        return;
      }

      if (elapsed < firstPhaseEnd) {
        const firstPhaseProgress = elapsed / firstPhaseEnd;
        const easedFirstPhase = gsap.parseEase("power3.inOut")(firstPhaseProgress);

        if (this.isMobile) {
          // Mobile Animation - Phase 1
          const phase1Top = this.interpolatePoints(
            '-50,0 50,40 150,0',
            '-50,0 50,40 -50,0',
            easedFirstPhase
          );

          const phase1Right = this.interpolatePoints(
            '100,20 50,40 100,60',
            '100,60 50,40 100,60',
            easedFirstPhase
          );

          const phase1Left = '0,20 100,60 100,100 0,100';

          this.topPolygon.setAttribute('points', phase1Top);
          this.rightPolygon.setAttribute('points', phase1Right);
          this.leftPolygon.setAttribute('points', phase1Left);
        } else {
          // Desktop Animation - Phase 1 (existing desktop code)
          const phase1Top = this.interpolatePoints(
            '0,0 100,0 65,50',
            '0,0 50,0 65,50',
            easedFirstPhase
          );

          const phase1Right = this.interpolatePoints(
            '100,0 65,50 65,100 100,100',
            '100,100 65,50 65,100 100,100',
            easedFirstPhase
          );

          const phase1Left = '0,0 65.1,50 65.1,100 0,100';

          this.topPolygon.setAttribute('points', phase1Top);
          this.rightPolygon.setAttribute('points', phase1Right);
          this.leftPolygon.setAttribute('points', phase1Left);
        }

      } else {
        // Phase 2
        const secondPhaseProgress = (elapsed - secondPhaseStart) / (this.duration - secondPhaseStart);
        const easedSecondPhase = gsap.parseEase("power3.inOut")(secondPhaseProgress);

        if (this.isMobile) {
          // Mobile Animation - Phase 2
          // Top und Right behalten ihre Endpositionen aus Phase 1
          const topPoints = '-50,0 50,40 -50,0';
          const rightPoints = '100,60 50,40 100,60';

          // Nur Left animiert in Phase 2
          const phase2Left = this.interpolatePoints(
            '0,20 100,60 100,100 0,100',
            '0,20 0,100 100,100 0,100',
            easedSecondPhase
          );

          this.topPolygon.setAttribute('points', topPoints);
          this.rightPolygon.setAttribute('points', rightPoints);
          this.leftPolygon.setAttribute('points', phase2Left);
        } else {
          // Desktop Animation - Phase 2 (existing desktop code)
          const phase2Top = this.interpolatePoints(
            '0,0 50,0 65,50',
            '0,0 -50,0 0,100',
            easedSecondPhase
          );

          const phase2Right = this.interpolatePoints(
            '100,100 65,50 65,100 100,100',
            '100,100 0,100 65,100 100,100',
            easedSecondPhase
          );

          const phase2Left = this.interpolatePoints(
            '0,0 65.1,50 65.1,100 0,100',
            '0,0 0,100 65,100 0,100',
            easedSecondPhase
          );

          this.topPolygon.setAttribute('points', phase2Top);
          this.rightPolygon.setAttribute('points', phase2Right);
          this.leftPolygon.setAttribute('points', phase2Left);
        }
      }

      requestAnimationFrame(animate);
    };

    requestAnimationFrame(animate);
  }

  interpolatePoints(start, end, progress) {
    const startPoints = start.split(' ').map(point => point.split(',').map(Number));
    const endPoints = end.split(' ').map(point => point.split(',').map(Number));

    return startPoints.map((point, i) => {
      const x = point[0] + (endPoints[i][0] - point[0]) * progress;
      const y = point[1] + (endPoints[i][1] - point[1]) * progress;
      return `${x},${y}`;
    }).join(' ');
  }
} export default HeroAnimation;
